import axios from 'axios';
import apiUrls from '@/setup/apiUrls.json';
import { getFormattedLocale } from '@/setup/i18n.js';
import useNotificationStore from '@/stores/notification';

// add appropriate header to all requests so that the BE can determine if it's an ajax request
axios.interceptors.request.use((config) => {
  config.headers['X-Requested-With'] = 'XMLHttpRequest';

  return config;
});

/**
 * Pushes an array of messages to the notification handler.
 *
 * @param {Array} messages - An array of messages.
 * @param {Object} options - Display options for the notifications.
 */
function showNotifications(messages, options) {
  if (!Array.isArray(messages)) {
    return;
  }

  messages.forEach((message) => {
    useNotificationStore().showNotification({
      ...options,
      message,
    });
  });
}

/**
 * Handles successful ajax requests.
 *
 * @param {Object} response - Response object.
 * @param {Object} [notificationOptions] - Additional notification options.
 *
 * @returns {Object}
 */
function handleSuccess(response, notificationOptions) {
  const { messages } = response?.data || {};

  if (messages && !notificationOptions?.preventNotifications) {
    showNotifications(messages, notificationOptions);
  }

  return response || {};
}

/**
 * Handles axios error responses.
 *
 * @param {Object} error - An axios error object.
 * @param {Object} error.response - The response data.
 * @param {Object} [notificationOptions] - Additional notification options.
 *
 * @returns {Promise<never>}
 */
function handleError(error, notificationOptions) {
  const { messages } = error?.response?.data || {};

  if (notificationOptions?.preventNotifications) {
    return Promise.reject(error);
  }

  if (messages) {
    showNotifications(messages, notificationOptions);
  } else if (!axios.isCancel(error)) {
    useNotificationStore().showUnknownError();
  }

  return Promise.reject(error);
}

const api = {
  /**
   * Gets the url for the given 'urlKey'. The method also accepts an Object of interpolation values.
   * Note: '{locale}' will be auto-replaced.
   *
   * @param {String} urlKey - The key for the requested url.
   * @param {Object} [values] - An Object of key/value pairs. The related '{key}' in the URL will be replaced with its value.
   *
   * @returns {String}
   */
  getUrl(urlKey, values) {
    let url = apiUrls[urlKey];

    if (!url) {
      throw new Error(`Unable to find an API url with the identifier ${urlKey}.`);
    }

    if (values) {
      Object.entries(values).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, value);
      });
    }

    const basePath = window?.piniaWorkaround?.basePath || 'base-path-not-found';

    return url.replace('{locale}', getFormattedLocale()).replace('{basePath}', basePath);
  },

  /**
   * Runs a head request with given url with given url params.
   *
   * @param {String} url - Url to get.
   * @param {Object} [config] - Url parameters which will be attached to the url.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Promise} Promise with response data or error.
   */
  head(url, config, notificationOptions) {
    return axios
      .head(url, config)
      .then((response) => handleSuccess(response, notificationOptions))
      .catch((error) => handleError(error, notificationOptions));
  },

  /**
   * Runs a get request with given url with given url params.
   *
   * @param {String} url - Url to get.
   * @param {Object} [config] - Url parameters which will be attached to the url.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Promise} Promise with response data or error.
   */
  get(url, config, notificationOptions) {
    return axios
      .get(url, config)
      .then((response) => handleSuccess(response, notificationOptions))
      .catch((error) => handleError(error, notificationOptions));
  },

  /**
   * Runs a post request with a given url and payload.
   *
   * @param {String} url - Url to post to.
   * @param {Object} [data] - Post payload which will be attached to the request.
   * @param {Object} [config] - Axios request configuration.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Promise} Promise with response data or error.
   */
  post(url, data, config, notificationOptions) {
    return axios
      .post(url, data, config)
      .then((response) => handleSuccess(response, notificationOptions))
      .catch((error) => handleError(error, notificationOptions));
  },

  /**
   * Runs a patch request with a given url and payload.
   *
   * @param {String} url - Url to patch to.
   * @param {Object} [data] - Patch payload which will be attached to the request.
   * @param {Object} [config] - Axios request configuration.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Promise} Promise with response data or error.
   */
  patch(url, data, config, notificationOptions) {
    return axios
      .patch(url, data, config)
      .then((response) => handleSuccess(response, notificationOptions))
      .catch((error) => handleError(error, notificationOptions));
  },

  /**
   * Runs a delete request with a given url and payload.
   *
   * @param {String} url - Url to send the delete to.
   * @param {Object} [config] - Axios request configuration.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Promise} Promise with response data or error.
   */
  delete(url, config, notificationOptions) {
    return axios
      .delete(url, config)
      .then((response) => handleSuccess(response, notificationOptions))
      .catch((error) => handleError(error, notificationOptions));
  },
};

/**
 * Constructor for the unique get method.
 *
 * @returns {Promise}
 */
export const uniqueGet = (() => {
  const call = {};

  /**
   * Creates a new request which cancels the one before. This means there can only be one request with the same id at a time.
   *
   * @param {String} url - The url for the get request.
   * @param {String} id - The id of the request. Only requests with the same id will be cancelled.
   * @param {Object} config - The additional request config object.
   * @param {Object} [notificationOptions] - Display options for notification.
   *
   * @returns {Function}
   */
  return (url, id, config, notificationOptions) => {
    if (call[id]) {
      call[id].cancel();
    }

    call[id] = axios.CancelToken.source();

    const mergedConfig = {
      ...config,
      cancelToken: call[id].token,
    };

    return api
      .get(url, mergedConfig, notificationOptions)
      .then((response) => {
        delete call[id];

        return response;
      })
      .catch((error) => {
        const errorName = error && error.name;
        const errorCode = error && error.code;

        if (axios.isCancel(error) || errorName === 'AbortError' || errorCode === 'ECONNABORTED') {
          return {
            isCancelled: true,
          };
        }

        throw new Error(`uniqueGet request failed: ${url}, ${id}`);
      });
  };
})();

export default api;
